import { Trans, useLingui } from "@lingui/react/macro";
import { startAuthentication, WebAuthnError } from "@simplewebauthn/browser";
import { type ReactNode, useState } from "react";

import type { ChallengeVerifyMethod } from "../../../api/api-connector";

import { ErrorCode, MFAMethod } from "../../../api";
import {
  ApiConnector,
  apiErrorCode,
  RequestError,
} from "../../../api/api-connector";
import { errorText } from "../../../app/error-text";
import { maybeReportError } from "../../../app/errors";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Button } from "../../../components/ui-kit/button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { useCodeErrorText } from "./code-error";
import { BackupCodeInput, OtpCodeInput } from "./code-input";
import {
  isDismissedPrompt,
  usePasskeyPrompt,
  usePasskeySupport,
} from "./passkey";

type Step =
  | { kind: "code" }
  /** The challenge is gone; only a fresh login can produce another. */
  | { kind: "expired" }
  /** Too many failed attempts — the backend discarded the challenge. */
  | { kind: "exhausted" };

/**
 * The second factor for an `mfa_required` response — from a login, or from the
 * fresh login that re-authenticating asks for. The only ways out are a correct
 * code or giving up; what a verified challenge grants is up to `onVerified`.
 */
export function MfaVerification({
  api,
  challengeId,
  methods,
  onVerified,
  onCancel,
  cancelLabel,
}: {
  api: ApiConnector;
  challengeId: string;
  methods: MFAMethod[];
  onVerified: () => void;
  onCancel: () => void;
  cancelLabel: ReactNode;
}) {
  const { t } = useLingui();
  const verifyErrorText = useCodeErrorText();
  const totpEnrolled = methods.includes(MFAMethod.totp);
  const passkeyEnrolled = methods.includes(MFAMethod.passkey);
  const passkeySupport = usePasskeySupport();
  const passkeyUsable = passkeyEnrolled && passkeySupport.supported;
  const [step, setStep] = useState<Step>({ kind: "code" });
  // Backup codes are never listed in `methods`, but `/challenge/verify` takes
  // one whenever the account still holds any — so where neither of the others
  // can be used, there is still a way in here.
  const [method, setMethod] = useState<MFAMethod>(
    passkeyUsable
      ? MFAMethod.passkey
      : totpEnrolled
        ? MFAMethod.totp
        : MFAMethod.backup_code,
  );
  const [error, setError] = useState<unknown>();
  // Bumped to remount the input, which is how a rejected code gets cleared.
  const [attempt, setAttempt] = useState(0);
  const passkeyPrompt = usePasskeyPrompt();

  async function verifyCode(method: ChallengeVerifyMethod, code: string) {
    setError(undefined);

    try {
      await api.verifyChallenge(challengeId, method, code);
      onVerified();
    } catch (error) {
      maybeReportError(error);
      if (endedTheChallenge(error)) {
        return;
      }

      if (apiErrorCode(error) === ErrorCode.MFA_INVALID_CODE) {
        // A wrong code leaves the challenge usable, so this is a plain retry.
        // The code is cleared — TOTP expires, backup codes are single-use.
        setAttempt((previous) => previous + 1);
      }

      setError(error);
    }
  }

  /**
   * Move a challenge-level failure onto the step that says so.
   *
   * @returns whether the challenge is gone, leaving nothing to retry.
   */
  function endedTheChallenge(error: unknown) {
    if (apiErrorCode(error) === ErrorCode.RATE_LIMIT_EXCEEDED) {
      setStep({ kind: "exhausted" });
      return true;
    }

    // Any 401 other than a rejected code means the challenge itself is gone —
    // expired, or already consumed by a concurrent attempt.
    if (
      error instanceof RequestError &&
      error.response.status === 401 &&
      apiErrorCode(error) !== ErrorCode.MFA_INVALID_CODE
    ) {
      setStep({ kind: "expired" });
      return true;
    }

    return false;
  }

  async function verifyPasskey() {
    const isCurrent = passkeyPrompt.start();
    setError(undefined);

    try {
      // Fetched only now, not ahead of time: the backend keeps the passkey
      // challenge only as long as the browser's prompt may stay open.
      const options = await api.startPasskeyVerification(challengeId);
      if (!isCurrent()) {
        return;
      }

      const credential = await startAuthentication({ optionsJSON: options });
      if (!isCurrent()) {
        return;
      }

      await api.verifyPasskey(challengeId, credential);
      onVerified();
    } catch (error) {
      // Called off by switching to another method.
      if (!isCurrent()) {
        return;
      }

      // A dismissed prompt is the user's choice. Anything else the browser
      // refuses — a domain mismatch, say — is a fault worth hearing about.
      if (!isDismissedPrompt(error)) {
        maybeReportError(error);
      }

      if (endedTheChallenge(error)) {
        return;
      }

      // A rejected passkey leaves the challenge usable, like a wrong code.
      setError(error);
    }
  }

  /** Why the last passkey attempt didn't work. */
  function passkeyErrorText(error: unknown) {
    if (error instanceof WebAuthnError) {
      return t`Your passkey wasn't used. Please try again, or choose another way below.`;
    }

    if (apiErrorCode(error) === ErrorCode.MFA_INVALID_CODE) {
      return t`That passkey couldn't be verified. Please try again.`;
    }

    return errorText(error);
  }

  function switchMethod(next: MFAMethod) {
    passkeyPrompt.cancel();
    setMethod(next);
    setError(undefined);
  }

  if (step.kind !== "code") {
    return (
      <div
        className="flex flex-col items-center"
        data-testid="mfa-verification"
      >
        <ErrorMessage
          message={
            step.kind === "exhausted" ? (
              // The lockout is on the account, not just the challenge, and
              // outlives a fresh login — so don't promise logging back in works.
              <Trans>
                Too many failed attempts. Please wait a few minutes and log in
                again.
              </Trans>
            ) : (
              <Trans>Your login expired before it was verified.</Trans>
            )
          }
        />
        <Button className="mt-4" onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
    );
  }

  // Every enrolled method other than the current one. Backup codes are always
  // offered, for the reason noted above.
  const offerable: {
    method: MFAMethod;
    label: ReactNode;
    enrolled: boolean;
    /** Why it can't be used here, if it can't. */
    blocked?: string;
  }[] = [
    {
      method: MFAMethod.passkey,
      label: <Trans>Use your passkey</Trans>,
      enrolled: passkeyEnrolled,
      blocked: passkeySupport.unsupportedReason,
    },
    {
      method: MFAMethod.totp,
      label: <Trans>Use your authenticator app</Trans>,
      enrolled: totpEnrolled,
    },
    {
      method: MFAMethod.backup_code,
      label: <Trans>Use a backup code</Trans>,
      enrolled: true,
    },
  ];

  const alternatives = offerable.filter(
    (alternative) => alternative.enrolled && alternative.method !== method,
  );

  // Switching methods clears the error, so any error here is the current one's.
  let errorMessage = "";
  if (error) {
    errorMessage =
      method === MFAMethod.passkey
        ? passkeyErrorText(error)
        : verifyErrorText(error);
  }

  return (
    <div className="flex flex-col items-center" data-testid="mfa-verification">
      <h2 className="text-xl font-semibold mb-4">
        <Trans>Two-factor authentication</Trans>
      </h2>

      {/* Capped on its own — the backup-code row is wider than this. */}
      <p className="text-sm text-center max-w-80">{instructionsFor(method)}</p>

      {method === MFAMethod.passkey && (
        <Button variant="primary" className="mt-6" onClick={verifyPasskey}>
          <Trans>Use your passkey</Trans>
        </Button>
      )}

      {method === MFAMethod.totp && (
        <OtpCodeInput
          key={attempt}
          submitLabel={t`Verify`}
          onSubmit={(code) => verifyCode(MFAMethod.totp, code)}
        />
      )}

      {method === MFAMethod.backup_code && (
        <BackupCodeInput
          key={attempt}
          submitLabel={t`Verify`}
          onSubmit={(code) => verifyCode(MFAMethod.backup_code, code)}
        />
      )}

      <ErrorMessage message={errorMessage} />

      <div className="flex flex-col items-center gap-2 mt-4">
        {alternatives.map((alternative) => (
          <Tooltip key={alternative.method} content={alternative.blocked}>
            <a
              aria-disabled={alternative.blocked !== undefined}
              onClick={() => {
                if (alternative.blocked === undefined) {
                  switchMethod(alternative.method);
                }
              }}
              className="text-xs text-brand-primary hover:underline cursor-pointer aria-disabled:opacity-50 aria-disabled:no-underline aria-disabled:cursor-default"
            >
              {alternative.label}
            </a>
          </Tooltip>
        ))}

        <a
          onClick={onCancel}
          className="text-xs text-brand-primary hover:underline cursor-pointer"
        >
          {cancelLabel}
        </a>
      </div>
    </div>
  );
}

function instructionsFor(method: MFAMethod) {
  switch (method) {
    case MFAMethod.passkey:
      return <Trans>Confirm it's you with your passkey.</Trans>;

    case MFAMethod.totp:
      return <Trans>Enter the code from your authenticator app.</Trans>;

    case MFAMethod.backup_code:
      return (
        <Trans>
          Enter one of the backup codes you saved when you set up two-factor
          authentication. Each code works once.
        </Trans>
      );
  }
}
